import React, { useState, useEffect } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { 
  PersonAdd, 
  PersonRemove, 
  Person,
  Warning,
  Group 
} from "@mui/icons-material";
import { useImpersonation } from "../hooks/useImpersonation";
import { useBackendUserInfo } from "../hooks/useBackendUserInfo";
import { 
  ImpersonationRequest, 
  TestUser,
  getTestUsers 
} from "../functions/requests/impersonation";
import { OidcConfigurationNameContext } from "../contexts/OidcConfigurationNameContext";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import { useContext } from "react";

export const ImpersonationDropdown: React.FC = () => {
  const { oidcConfigurationName } = useContext(OidcConfigurationNameContext);
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const { userInfo } = useBackendUserInfo();
  const { 
    currentImpersonation, 
    isImpersonating, 
    startImpersonation, 
    stopImpersonation, 
    loading,
    error 
  } = useImpersonation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testUsers, setTestUsers] = useState<TestUser[]>([]);
  const [selectedTestUser, setSelectedTestUser] = useState<string>("");
  const [customUser, setCustomUser] = useState<ImpersonationRequest>({
    target_user_id: "",
    target_user_email: "",
    target_user_name: "",
    target_user_groups: [],
  });
  const [customGroupInput, setCustomGroupInput] = useState("");
  const [loadingTestUsers, setLoadingTestUsers] = useState(false);

  // Debug logging
  console.log("ImpersonationDropdown - userInfo:", userInfo);
  console.log("ImpersonationDropdown - can_impersonate:", userInfo?.can_impersonate);
  console.log("ImpersonationDropdown - oidcAccessToken:", oidcAccessToken);
  
  // Debug the JWT token payload
  if (oidcAccessToken?.accessTokenPayload) {
    console.log("JWT payload:", oidcAccessToken.accessTokenPayload);
    console.log("JWT aud claim:", oidcAccessToken.accessTokenPayload.aud);
    console.log("JWT appid claim:", oidcAccessToken.accessTokenPayload.appid);
    console.log("JWT azp claim:", oidcAccessToken.accessTokenPayload.azp);
  }

  // TEMPORARY: Override for testing - remove this after testing
  const canImpersonateOverride = false; // Set to false to disable override
  
  // Don't render if user can't impersonate (with temporary override)
  if (!userInfo?.can_impersonate && !canImpersonateOverride) {
    console.log("ImpersonationDropdown - Not rendering: can_impersonate is false/undefined");
    return null;
  }

  if (canImpersonateOverride) {
    console.log("ImpersonationDropdown - Rendering with TEMPORARY OVERRIDE for testing");
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStartImpersonation = () => {
    setDialogOpen(true);
    handleMenuClose();
    loadTestUsers();
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      handleMenuClose();
    } catch (err) {
      console.error("Failed to stop impersonation:", err);
    }
  };

  const loadTestUsers = async () => {
    if (!oidcAccessToken?.accessToken) return;
    
    setLoadingTestUsers(true);
    try {
      const response = await getTestUsers(oidcAccessToken);
      setTestUsers(response.test_users);
    } catch (err) {
      console.error("Failed to load test users:", err);
    } finally {
      setLoadingTestUsers(false);
    }
  };

  const handleTestUserSelect = (userId: string) => {
    const testUser = testUsers.find(u => u.target_user_id === userId);
    if (testUser) {
      setCustomUser({
        target_user_id: testUser.target_user_id,
        target_user_email: testUser.target_user_email || "",
        target_user_name: testUser.target_user_name || "",
        target_user_groups: testUser.target_user_groups || [],
      });
    }
    setSelectedTestUser(userId);
  };

  const handleAddGroup = () => {
    if (customGroupInput.trim() && !customUser.target_user_groups?.includes(customGroupInput.trim())) {
      setCustomUser(prev => ({
        ...prev,
        target_user_groups: [...(prev.target_user_groups || []), customGroupInput.trim()]
      }));
      setCustomGroupInput("");
    }
  };

  const handleRemoveGroup = (groupToRemove: string) => {
    setCustomUser(prev => ({
      ...prev,
      target_user_groups: (prev.target_user_groups || []).filter(group => group !== groupToRemove)
    }));
  };

  const handleConfirmImpersonation = async () => {
    if (!customUser.target_user_id.trim()) {
      return;
    }

    try {
      await startImpersonation(customUser);
      setDialogOpen(false);
      setSelectedTestUser("");
      setCustomUser({
        target_user_id: "",
        target_user_email: "",
        target_user_name: "",
        target_user_groups: [],
      });
    } catch (err) {
      console.error("Failed to start impersonation:", err);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedTestUser("");
    setCustomUser({
      target_user_id: "",
      target_user_email: "",
      target_user_name: "",
      target_user_groups: [],
    });
  };

  const isFormValid = customUser.target_user_id.trim().length > 0;

  return (
    <>
      <Tooltip title={isImpersonating ? "Impersonation Active - Click to manage" : "User Impersonation"}>
        <IconButton 
          onClick={handleMenuOpen}
          color={isImpersonating ? "warning" : "default"}
          sx={{ 
            border: isImpersonating ? "2px solid orange" : "none",
            animation: isImpersonating ? "pulse 2s infinite" : "none"
          }}
        >
          {isImpersonating ? <Warning /> : <PersonAdd />}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 250 }
        }}
      >
        {isImpersonating ? (
          <Box>
            <MenuItem disabled>
              <Box>
                <Typography variant="subtitle2" color="orange">
                  Currently Impersonating:
                </Typography>
                <Typography variant="body2">
                  {userInfo?.name || userInfo?.preferred_username}
                </Typography>
                {userInfo?.original_user && (
                  <Typography variant="caption" color="textSecondary">
                    Original: {userInfo.original_user.name || userInfo.original_user.preferred_username}
                  </Typography>
                )}
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleStopImpersonation} disabled={loading}>
              <PersonRemove sx={{ mr: 1 }} />
              Stop Impersonation
            </MenuItem>
          </Box>
        ) : (
          <MenuItem onClick={handleStartImpersonation} disabled={loading}>
            <PersonAdd sx={{ mr: 1 }} />
            Start Impersonation
          </MenuItem>
        )}
      </Menu>

      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Person sx={{ mr: 1 }} />
            Start User Impersonation
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Impersonation allows you to test RAG queries and RBAC permissions as different users. 
              All actions will be performed with the selected user's permissions.
            </Typography>
          </Alert>

          {loadingTestUsers ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Predefined Test Users</InputLabel>
              <Select
                value={selectedTestUser}
                onChange={(e) => handleTestUserSelect(e.target.value)}
                label="Predefined Test Users"
              >
                <MenuItem value="">Custom User...</MenuItem>
                {testUsers.map((user) => (
                  <MenuItem key={user.target_user_id} value={user.target_user_id}>
                    <Box>
                      <Typography variant="body2">
                        {user.target_user_name || user.target_user_id}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {user.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="User ID / Email"
            value={customUser.target_user_id}
            onChange={(e) => setCustomUser(prev => ({ ...prev, target_user_id: e.target.value }))}
            margin="normal"
            required
            helperText="The user identifier to impersonate (usually email address)"
          />

          <TextField
            fullWidth
            label="User Email"
            value={customUser.target_user_email}
            onChange={(e) => setCustomUser(prev => ({ ...prev, target_user_email: e.target.value }))}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Display Name"
            value={customUser.target_user_name}
            onChange={(e) => setCustomUser(prev => ({ ...prev, target_user_name: e.target.value }))}
            margin="normal"
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              User Groups:
            </Typography>
            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
              {customUser.target_user_groups?.map((group) => (
                <Chip
                  key={group}
                  label={group}
                  onDelete={() => handleRemoveGroup(group)}
                  size="small"
                  icon={<Group />}
                />
              ))}
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                placeholder="Add group ID"
                value={customGroupInput}
                onChange={(e) => setCustomGroupInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddGroup()}
                sx={{ flexGrow: 1 }}
              />
              <Button onClick={handleAddGroup} disabled={!customGroupInput.trim()}>
                Add
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleConfirmImpersonation} 
            variant="contained"
            disabled={!isFormValid || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <PersonAdd />}
          >
            Start Impersonation
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 